import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export const isIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const isAndroid = () => /android/i.test(navigator.userAgent);

export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

export const pushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export const permissionState = () => (('Notification' in window) ? Notification.permission : 'unsupported');

export const platformLabel = () => (isIOS() ? 'ios' : isAndroid() ? 'android' : 'desktop');

export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (e) {
    console.warn('SW registration failed', e);
    return null;
  }
}

const urlBase64ToUint8Array = (base64) => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export async function getExistingSubscription() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

// Returns { ok, reason } — reason: 'unsupported' | 'ios_not_installed' | 'denied' | 'error'
export async function subscribeToPush() {
  if (!pushSupported()) {
    return { ok: false, reason: isIOS() && !isStandalone() ? 'ios_not_installed' : 'unsupported' };
  }
  try {
    const reg = (await navigator.serviceWorker.getRegistration('/')) || (await registerServiceWorker());
    if (!reg) return { ok: false, reason: 'unsupported' };
    await navigator.serviceWorker.ready;
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') return { ok: false, reason: 'denied' };
    const { data } = await axios.get(`${API}/push/vapid-public-key`, auth());
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
    }
    await axios.post(`${API}/push/subscribe`, {
      subscription: sub.toJSON(),
      user_agent: navigator.userAgent,
      platform: platformLabel(),
      standalone: isStandalone(),
    }, auth());
    localStorage.setItem('push_subscribed', '1');
    return { ok: true };
  } catch (e) {
    console.warn('push subscribe failed', e);
    return { ok: false, reason: 'error', error: e?.response?.data?.detail || e?.message };
  }
}

export async function unsubscribeFromPush() {
  const sub = await getExistingSubscription();
  if (sub) {
    try { await axios.post(`${API}/push/unsubscribe`, { endpoint: sub.endpoint }, auth()); } catch {}
    await sub.unsubscribe();
  }
  localStorage.removeItem('push_subscribed');
}

// Silently re-sync an already-granted subscription with the backend (e.g. after a new login on the same phone)
export async function resyncPushIfGranted() {
  if (!pushSupported() || permissionState() !== 'granted') return;
  try {
    const reg = (await navigator.serviceWorker.getRegistration('/')) || (await registerServiceWorker());
    if (!reg) return;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    await axios.post(`${API}/push/subscribe`, {
      subscription: sub.toJSON(), user_agent: navigator.userAgent, platform: platformLabel(), standalone: isStandalone(),
    }, auth());
    localStorage.setItem('push_subscribed', '1');
  } catch {}
}
